import { Component, Input, Output, EventEmitter, OnInit, OnChanges, DoCheck, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { PyramidalShareholderPyramidComponent } from '../pyramidal-shareholder-pyramid/pyramidal-shareholder-pyramid.component';
import { PyramidalShareholderPyramidDto } from '../../../models/api/pyramidalShareholderPyramidDto';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

@Component({
  selector: 'app-pyramidal-shareholder-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    PyramidalShareholderPyramidComponent,
    TextNormalizeDirective
  ],
  templateUrl: './pyramidal-shareholder-form.component.html',
  styleUrls: ['./pyramidal-shareholder-form.component.scss']
})
export class PyramidalShareholderFormComponent implements OnInit, OnChanges, DoCheck {
  @Input() form!: FormGroup;
  @Input() submitButtonText = 'Submit';
  @Input() isEditMode = false;
  @Input() initialTabIndex = 0;
  @Input() pyramidsByYear: { [year: number]: PyramidalShareholderPyramidDto[] } = {};
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() tabChange = new EventEmitter<number>();
  @Output() editPyramidItem = new EventEmitter<string>();
  @Output() deletePyramidItem = new EventEmitter<{ pyramidId: string, year?: number }>();

  selectedTabIndex = 0;

  private lutService = inject(LutService);

  categories: LargeShareholderCategory[] = [];
  subcategories: LargeShareholderCategory[] = [];
  private allCategories: LargeShareholderCategory[] = []; // Cache all categories
  private categoriesLoaded = false; // Track if categories have been loaded
  private lastCategoryId: number | null = null; // Track last category ID to detect changes

  constructor(private confirmationDialogService: ConfirmationDialogService) {
    // Load large shareholder categories from LUT service and cache them
    this.lutService.getLargeShareholderCategories().subscribe(data => {
      if (data) {
        this.allCategories = data; // Cache all categories
        this.categories = data.filter(c => c.parentId === null).sort((a, b) => a.name.localeCompare(b.name));
        this.categoriesLoaded = true;

        // Initialize subcategories after categories are loaded
        this.initializeSubcategories();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Watch for form input changes - this handles when parent component patches the form
    if (changes['form'] && !changes['form'].firstChange) {
      // Form reference changed, re-initialize subcategories if categories are loaded
      if (this.categoriesLoaded) {
        this.initializeSubcategories();
      }
    }
  }

  ngDoCheck() {
    // Check on every change detection cycle if the category has changed
    // This catches changes even when patched with emitEvent: false
    if (this.categoriesLoaded && this.form) {
      const currentCategory = this.form.get('category')?.value;
      const currentCategoryId = currentCategory?.id ?? null;

      if (currentCategoryId !== this.lastCategoryId) {
        // Category has changed, update subcategories
        this.lastCategoryId = currentCategoryId;
        this.initializeSubcategories();
      }
    }
  }

  ngOnInit() {
    // Set initial tab index
    this.selectedTabIndex = this.initialTabIndex;

    // Watch category changes to update subcategories (when user selects from dropdown)
    this.form.get('category')?.valueChanges.subscribe((category: LargeShareholderCategory) => {
      if (category) {
        // Use cached categories instead of API call
        this.subcategories = this.allCategories
          .filter(c => c.parentId === category.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        // Clear subcategory if the selected one is no longer valid
        const currentSubcategory = this.form.get('subcategory')?.value;
        if (currentSubcategory && currentSubcategory.parentId !== category.id) {
          this.form.patchValue({ subcategory: null });
        }
      } else {
        this.subcategories = [];
        this.form.patchValue({ subcategory: null });
      }
    });

    // Initial subcategory setup if categories are already loaded
    if (this.categoriesLoaded) {
      this.initializeSubcategories();
    }
  }

  /**
   * Initialize subcategories based on the current category value
   * This is called after categories are loaded from the API
   */
  private initializeSubcategories() {
    const currentCategory = this.form.get('category')?.value;
    if (currentCategory) {
      this.subcategories = this.allCategories
        .filter(c => c.parentId === currentCategory.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  compareById(option1: any, option2: any): boolean {
    return option1 && option2 ? option1.id === option2.id : option1 === option2;
  }

  onSubmit() {
    if (this.form.valid) {
      const dialogObservable = this.isEditMode
        ? this.confirmationDialogService.openUpdateConfirmationDialog()
        : this.confirmationDialogService.openCreateConfirmationDialog();

      dialogObservable.subscribe((result: boolean) => {
        if (result) {
          this.submit.emit();
        }
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  isPyramidTabActive(): boolean {
    return this.isEditMode && this.selectedTabIndex === 1;
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    this.tabChange.emit(index);
  }

  onEditPyramidItem(pyramidId: string) {
    this.editPyramidItem.emit(pyramidId);
  }

  onDeletePyramidItem(event: { pyramidId: string, year?: number }) {
    this.deletePyramidItem.emit(event);
  }
}
